import React, { useEffect, useState } from "react";
import "../Styles/CompanyDashboard.css";
import { Users, UserCheck, Briefcase, AlertCircle, Loader } from "lucide-react";
import { useParams } from "react-router-dom";
import { API_BASE_URL } from "../apiConfig";
import UsersTable from "../Components/Common/UsersTable";

const CompanyDashboard = () => {
  const { companyId } = useParams();
  const [companyName, setCompanyName] = useState("Company Dashboard");
  const [stats, setStats] = useState({ totalUsers: 0, activeUsers: 0 });
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [searchTerm, setSearchTerm] = useState("");
  
  const [currentPage, setCurrentPage] = useState(1);
  const [usersPerPage] = useState(10);

  useEffect(() => {
    const fetchCompanyDashboardData = async () => {
      try {
        setLoading(true);
        const response = await fetch(`${API_BASE_URL}/companies/${companyId}/dashboard`, {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("idToken")}`,
          },
        });
          if (response.status === 401 || response.status === 403) {
          localStorage.clear();
          window.location.href = '/';
          return;
        }
        if (!response.ok) {
          throw new Error("Failed to fetch company dashboard data");
        }

        const data = await response.json();
        if (data.success) {
          setStats(data.stats);
          setUsers(data.users);
          if (data.company && data.company.name) {
            setCompanyName(data.company.name);
          }
        } else {
          setError(data.message || "Failed to fetch data");
        }
      } catch (err) {
        console.error("Error fetching company dashboard data:", err);
        setError(err.message || "An error occurred");
      } finally {
        setLoading(false);
      }
    };

    fetchCompanyDashboardData();
  }, [companyId]);

  const filteredUsers = users.filter(user => 
    user.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    user.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    user.employeeId?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const indexOfLastUser = currentPage * usersPerPage;
  const indexOfFirstUser = indexOfLastUser - usersPerPage;
  const currentUsers = filteredUsers.slice(indexOfFirstUser, indexOfLastUser);
  
  const paginate = (pageNumber) => setCurrentPage(pageNumber);
  
  const totalPages = Math.ceil(filteredUsers.length / usersPerPage);

  const handleSearchChange = (value) => {
    setSearchTerm(value);
    setCurrentPage(1);
  };

  if (loading) {
    return (
      <div className="cd-loading-container">
        <Loader size={40} className="cd-loading-spinner" />
        <p>Loading dashboard data...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="cd-error-container">
        <AlertCircle size={40} color="#ef4444" />
        <h2>Error Loading Dashboard</h2>
        <p>{error}</p>
        <button 
          className="cd-retry-button"
          onClick={() => window.location.reload()}
        >
          Retry
        </button>
      </div>
    );
  }

  return (
    <div className="cd-container">
      <div className="ap-wrap-content">
     <div className="cd-header">
  <div className="cd-header-content">
    <div className="cd-header-icon">
      <Briefcase size={28} color="#f3f4f6" />
    </div>
    <h1 className="cd-title">{companyName}</h1>
  </div>
</div>


        <div className="cd-stats-grid">
          <div className="cd-stat-card cd-stat-card-purple">
            <div className="cd-stat-icon-container">
              <Users size={24} color="#6366F1" />
            </div>
            <div className="cd-stat-content">
              <h3 className="cd-stat-title">Total Users</h3>
              <p className="cd-stat-value">{stats.totalUsers}</p>
            </div>
          </div>

          <div className="cd-stat-card cd-stat-card-green">
            <div className="cd-stat-icon-container">
              <UserCheck size={24} color="#10B981" />
            </div>
            <div className="cd-stat-content">
              <h3 className="cd-stat-title">Active Users</h3>
              <p className="cd-stat-value">{stats.activeUsers}</p>
            </div>
          </div>
        </div>

        <UsersTable 
          users={currentUsers}
          currentPage={currentPage}
          totalPages={totalPages}
          searchTerm={searchTerm}
          onSearchChange={handleSearchChange}
          onPageChange={paginate}
        />
      </div>
    </div>
  );
};

export default CompanyDashboard;
